import { Dropzone, FilesPreview, getRejectedFilesList, notify, SkeletonBox } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { formatMegabytesToBytes } from '../../../../../../utils/formatMegabytesToBytes'
import { getImageClassName } from '../../../../../../utils/getClassName'
import { UploadFilesErrorMessage } from '../../../../../attachments/elements/UploadFilesErrorMessage'
import { acceptedZervantInvoiceLogoFileFormats } from '../../constants/acceptedZervantInvoiceLogoFileFormats'
import { INVOICE_LOGO_WRAPPER_HEIGHT } from '../../constants/invoiceLogoWrapperHeight'
import type { UploadZervantInvoiceLogoResponse } from '../../query-api'
import { useDeleteZervantInvoiceLogo } from './hooks/useDeleteZervantInvoiceLogo'
import { useUploadZervantInvoiceLogo } from './hooks/useUploadZervantInvoiceLogo'
import * as Styled from './styles'

interface InvoiceLogoProps {
  contentText?: string
  footerText?: string
  isLoading?: boolean
  logoUrl?: string | null
  onDeleteSuccess: () => void
  onUploadSuccess: (data: UploadZervantInvoiceLogoResponse) => void
}

export const InvoiceLogo = ({
  contentText,
  footerText,
  isLoading,
  logoUrl,
  onUploadSuccess,
  onDeleteSuccess,
}: InvoiceLogoProps): ReactElement => {
  const { t } = useTranslation()

  const { upload: uploadLogo, isProcessing: isUploadingLogo } = useUploadZervantInvoiceLogo({
    onSuccess: (data: UploadZervantInvoiceLogoResponse) => {
      onUploadSuccess(data)
    },
  })

  const { delete: deleteLogo } = useDeleteZervantInvoiceLogo({
    onSuccess: () => {
      onDeleteSuccess()
    },
  })

  const handleDropAccepted = useCallback(
    ([file]: File[]) => {
      uploadLogo(file)
    },
    [uploadLogo],
  )

  const handleDropRejected = useCallback(
    (filesRejected: FileRejection[]) => {
      const uploadFilesErrors = getRejectedFilesList(filesRejected)
      notify({
        id: NotificationKeys.ExternalInvoiceSettingsLogoRejected,
        message: UploadFilesErrorMessage({ uploadFilesErrors }),
        title: t('file.rejected.count', { count: filesRejected.length }),
        variant: 'warning',
      })
    },
    [t],
  )

  const handleEditButtonClick = useCallback(
    (file: File) => {
      uploadLogo(file)
    },
    [uploadLogo],
  )

  const handleDeleteButtonClick = useCallback(() => {
    deleteLogo()
  }, [deleteLogo])

  if (isLoading || isUploadingLogo) {
    return <SkeletonBox height={INVOICE_LOGO_WRAPPER_HEIGHT} fullWidth />
  }

  return (
    <Styled.InvoiceLogoWrapper>
      {logoUrl ? (
        <FilesPreview
          bordered
          className={getImageClassName()}
          files={[{ src: logoUrl }]}
          fitToHeight
          fitType="contain"
          key={logoUrl}
          onDelete={handleDeleteButtonClick}
          onEdit={handleEditButtonClick}
          withDeleteButton
          withEditButton
        />
      ) : (
        <Dropzone
          accept={acceptedZervantInvoiceLogoFileFormats}
          contentText={contentText ?? t('settings.organization.external_invoice.appearance.logo.drag_and_drop_here')}
          footerText={footerText}
          isUploading={isUploadingLogo}
          maxFiles={1}
          maxSize={formatMegabytesToBytes(5)}
          onDropAccepted={handleDropAccepted}
          onDropRejected={handleDropRejected}
        />
      )}
    </Styled.InvoiceLogoWrapper>
  )
}
